/**
 * Small geo-math helpers shared across the app. Distance and bearing logic was
 * previously copy-pasted in several spots (gpxParser, LiveLocationContext,
 * MapViewPanel, RecommendedAdventuresWidget); prefer importing from here.
 */

export interface Coords {
  latitude: number
  longitude: number
}

const EARTH_RADIUS_M = 6371000 // mean Earth radius in meters
const toRad = (deg: number) => (deg * Math.PI) / 180
const toDeg = (rad: number) => (rad * 180) / Math.PI

/** Great-circle distance between two points, in meters (Haversine). */
export function distanceMeters(a: Coords, b: Coords): number {
  const lat1 = toRad(a.latitude)
  const lat2 = toRad(b.latitude)
  const dLat = toRad(b.latitude - a.latitude)
  const dLon = toRad(b.longitude - a.longitude)

  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2)

  return EARTH_RADIUS_M * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))
}

/**
 * Initial compass bearing from `from` toward `to`, as a true heading in
 * degrees clockwise from north (0 = N, 90 = E, 180 = S, 270 = W). Suitable
 * for rotating a north-up arrow: `transform: rotate(${bearing}deg)`.
 */
export function bearingDegrees(from: Coords, to: Coords): number {
  const lat1 = toRad(from.latitude)
  const lat2 = toRad(to.latitude)
  const dLon = toRad(to.longitude - from.longitude)

  const x = Math.sin(dLon) * Math.cos(lat2)
  const y =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon)

  return (toDeg(Math.atan2(x, y)) + 360) % 360
}

const CARDINALS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const

/** 8-point compass label ('N', 'NE', …) for a 0–360 bearing. */
export function cardinalFromBearing(deg: number): string {
  const normalized = ((deg % 360) + 360) % 360
  return CARDINALS[Math.round(normalized / 45) % 8]
}

/**
 * Project a point onto segment A→B in a local planar (equirectangular) frame
 * centered on A. Returns the perpendicular distance (m) and the clamped
 * fraction t∈[0,1] of the projection along the segment.
 */
function projectToSegment(
  pLat: number,
  pLng: number,
  aLat: number,
  aLng: number,
  bLat: number,
  bLng: number
): { distance: number; t: number } {
  const mPerDegLat = 110540
  const mPerDegLng = 111320 * Math.cos((aLat * Math.PI) / 180)
  const bx = (bLng - aLng) * mPerDegLng
  const by = (bLat - aLat) * mPerDegLat
  const px = (pLng - aLng) * mPerDegLng
  const py = (pLat - aLat) * mPerDegLat
  const len2 = bx * bx + by * by
  let t = len2 === 0 ? 0 : (px * bx + py * by) / len2
  t = Math.max(0, Math.min(1, t))
  const cx = t * bx
  const cy = t * by
  return { distance: Math.hypot(px - cx, py - cy), t }
}

export interface PathPosition {
  /** Perpendicular distance (m) from the point to the nearest route segment. */
  distanceMeters: number
  /** Distance (m) along the route from the start to the projected point. */
  alongMeters: number
  /** Progress along the route, 0–1. */
  fraction: number
}

/**
 * Snap a point to a polyline route given as [lat, lng] tuples: find the nearest
 * segment by perpendicular distance, then measure how far along the route that
 * projection sits. Drives the "on route" check (distanceMeters) and "% complete"
 * (fraction) for the player route engine. Returns zeros for an empty path.
 *
 * Ported from trail-recorder/utils/trailGeo.ts so the player runtime doesn't
 * have to import across features.
 */
export function nearestOnPath(
  path: [number, number][],
  lat: number,
  lng: number
): PathPosition {
  if (path.length === 0) {
    return { distanceMeters: Infinity, alongMeters: 0, fraction: 0 }
  }
  if (path.length === 1) {
    return {
      distanceMeters: distanceMeters(
        { latitude: lat, longitude: lng },
        { latitude: path[0][0], longitude: path[0][1] }
      ),
      alongMeters: 0,
      fraction: 0,
    }
  }

  let cumulative = 0
  let best = { distance: Infinity, along: 0 }
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i]
    const b = path[i + 1]
    const segLen = distanceMeters(
      { latitude: a[0], longitude: a[1] },
      { latitude: b[0], longitude: b[1] }
    )
    const { distance, t } = projectToSegment(lat, lng, a[0], a[1], b[0], b[1])
    if (distance < best.distance) {
      best = { distance, along: cumulative + t * segLen }
    }
    cumulative += segLen
  }

  const fraction = cumulative > 0 ? Math.max(0, Math.min(1, best.along / cumulative)) : 0
  return { distanceMeters: best.distance, alongMeters: best.along, fraction }
}

/**
 * Extract the sub-polyline of `path` between two along-route distances (meters
 * from the start, as produced by `nearestOnPath().alongMeters`). The returned
 * line has interpolated vertices exactly at `startM` and `endM` plus every
 * original vertex in between, so it traces the same geometry. Returns an empty
 * array for a degenerate range or a path with fewer than two points.
 *
 * Used to tint the portion of a route the player has already walked (the spline
 * between two passed waypoints) without redrawing the whole line.
 */
export function sliceAlong(
  path: [number, number][],
  startM: number,
  endM: number
): [number, number][] {
  if (path.length < 2) return []
  const lo = Math.max(0, Math.min(startM, endM))
  const hi = Math.max(startM, endM)
  if (hi <= lo) return []

  const result: [number, number][] = []
  let cumulative = 0
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i]
    const b = path[i + 1]
    const segLen = distanceMeters(
      { latitude: a[0], longitude: a[1] },
      { latitude: b[0], longitude: b[1] }
    )
    const segStart = cumulative
    cumulative += segLen
    if (segLen <= 0 || cumulative < lo || segStart > hi) continue
    const tStart = Math.max(0, (lo - segStart) / segLen)
    const tEnd = Math.min(1, (hi - segStart) / segLen)
    const lerp = (t: number): [number, number] => [
      a[0] + (b[0] - a[0]) * t,
      a[1] + (b[1] - a[1]) * t,
    ]
    const p0 = lerp(tStart)
    const p1 = lerp(tEnd)
    if (result.length === 0) result.push(p0)
    const last = result[result.length - 1]
    if (last[0] !== p1[0] || last[1] !== p1[1]) result.push(p1)
  }
  return result.length >= 2 ? result : []
}

/**
 * Human-friendly distance string. Default 'imperial' (miles/feet) to match the
 * rest of the app; 'metric' yields km/m. Short distances use ft/m, longer ones
 * mi/km with one decimal.
 */
export function formatDistance(
  meters: number,
  unit: 'imperial' | 'metric' = 'imperial'
): string {
  if (unit === 'metric') {
    if (meters < 1000) return `${Math.round(meters)} m`
    return `${(meters / 1000).toFixed(1)} km`
  }
  const feet = meters * 3.28084
  if (feet < 1000) return `${Math.round(feet)} ft`
  return `${(feet / 5280).toFixed(1)} mi`
}
